import React, { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import {
  SlidersHorizontal,
  Search,
  X,
  RotateCcw,
  WifiOff,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  areaElementClasses,
  lineElementClasses,
} from '@mui/x-charts/LineChart';
import { useXScale, useDrawingArea } from '@mui/x-charts/hooks';
import {
  ChartsTooltipContainer,
  useAxesTooltip,
  type ChartsTooltipProps,
} from '@mui/x-charts/ChartsTooltip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DescriptionPopper from '@/components/DescriptionPopper';
import { buildSpectrumData, SPECTRUM_COLORS } from '@/utils/scanner';
import {
  SpectrumChartContainer,
  GradientDefsSvg,
  NetworkLegendColor,
  StyledSpectrumLineChart,
  HiddenSSIDLabel,
  SpectrumChartBox,
  BottomLegendContainer,
  LegendChipItem,
  LegendBadge,
  FilterBarContainer,
  FilterToggleChip,
  FilterPanelCard,
  FilterSearchWrapper,
  EmptyStateBox,
  TooltipChannelHeader,
  TooltipDotMarker,
  FilterCountTypography,
  ClearFilterChip,
  StyledRssiSlider,
  LegendCardHeader,
  LegendChipLabel,
  ChartTooltipCard,
  LegendNetworkCard,
  FilterLabelTypography,
  LegendTitleTypography,
  TooltipNetworkLabel,
  TooltipSignalValue,
  LegendSignalTypography,
  SliderBox,
  LegendHeaderRow,
  FilterPresetsWrapper,
  FilterActionsWrapper,
  FilterPanelMainRow,
  SliderHeaderRow,
  ChannelsButtonRow,
  LegendChipsWrapper,
  TooltipRowStack,
  TooltipLabelGroup,
} from './styles';

interface SpectrumChartProps {
  networks: WifiNetwork[];
}

const VALID_5GHZ_CHANNELS = [
  36, 40, 44, 48, 52, 56, 60, 64, 100, 104, 108, 112, 116, 120, 124, 128, 132,
  136, 140, 144, 149, 153, 157, 161, 165,
];

/** Glassmorphic Card tooltip rendered on chart hover */
function ChartHoverCardTooltip(props: ChartsTooltipProps) {
  const tooltipData = useAxesTooltip();
  const { t } = useTranslation();

  if (!tooltipData || tooltipData.length === 0) return null;

  const activeAxis = tooltipData[0];
  if (!activeAxis) return null;

  // filter series items that are active and have a valid dBm value (> -99.9 dBm)
  const activeItems = activeAxis.seriesItems.filter((item) => {
    if (item.value === null || item.value === undefined) return false;
    const num = Number(item.value);
    return !isNaN(num) && num > -99.9;
  });

  if (activeItems.length === 0) return null;

  const channelNumber =
    activeAxis.axisValue != null
      ? Number(activeAxis.axisValue).toFixed(1).replace(/\.0$/, '')
      : null;

  return (
    <ChartsTooltipContainer {...props}>
      <ChartTooltipCard>
        <Stack direction="column" spacing="8px" width="100%">
          {/* Channel Header */}
          {channelNumber && (
            <TooltipChannelHeader variant="caption">
              {t('scanner.channelPrefix', { channel: channelNumber })}
            </TooltipChannelHeader>
          )}

          {/* Network signal rows */}
          <Stack direction="column" spacing="6px">
            {activeItems.map((item) => (
              <TooltipRowStack key={String(item.seriesId)}>
                <TooltipLabelGroup>
                  <TooltipDotMarker color={item.color} />
                  <TooltipNetworkLabel variant="caption" noWrap>
                    {item.formattedLabel || String(item.seriesId)}
                  </TooltipNetworkLabel>
                </TooltipLabelGroup>

                <TooltipSignalValue variant="caption" color={item.color}>
                  {item.formattedValue || `${item.value} dBm`}
                </TooltipSignalValue>
              </TooltipRowStack>
            ))}
          </Stack>
        </Stack>
      </ChartTooltipCard>
    </ChartsTooltipContainer>
  );
}

interface HorizontalChannelLabelsProps {
  band: string;
  minCh: number;
  maxCh: number;
  channelsInUse: number[];
}

/** Renders horizontal channel labels and channel centerlines across the spectrum */
function HorizontalChannelLabels({
  band,
  minCh,
  maxCh,
  channelsInUse,
}: HorizontalChannelLabelsProps) {
  const xScale = useXScale();
  const drawingArea = useDrawingArea();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const channels = useMemo(() => {
    if (band === '2.4GHz') {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
    }
    return VALID_5GHZ_CHANNELS.filter(
      (ch) => ch >= minCh - 2 && ch <= maxCh + 2
    );
  }, [band, minCh, maxCh]);

  if (!xScale || !drawingArea) return null;

  const yLabelPos = drawingArea.top + drawingArea.height + (isMobile ? 16 : 20);
  const yLineStart = drawingArea.top;
  const yLineEnd = drawingArea.top + drawingArea.height;

  // Track rendered label X positions to prevent any collision
  const renderedPositions: number[] = [];
  const minLabelGap = band === '2.4GHz' ? (isMobile ? 18 : 34) : 24;

  return (
    <g className="custom-horizontal-channel-indicators">
      {channels.map((ch) => {
        const x = xScale(ch);
        if (x === undefined || isNaN(x)) return null;

        const isChannelInUse = channelsInUse.includes(ch);

        // Check if this label would collide with a previously rendered label
        const hasCollision = renderedPositions.some(
          (posX) => Math.abs(posX - x) < minLabelGap
        );

        const canRenderText = isChannelInUse || !hasCollision;

        if (canRenderText) {
          renderedPositions.push(x);
        }

        const labelText =
          band === '2.4GHz'
            ? isMobile
              ? `${ch}`
              : t('scanner.channelPrefix', { channel: ch })
            : `${ch}`;

        return (
          <g key={ch}>
            {/* Guide line down each channel center */}
            <line
              x1={x}
              y1={yLineStart}
              x2={x}
              y2={yLineEnd}
              stroke={
                isChannelInUse
                  ? 'rgba(255, 255, 255, 0.18)'
                  : 'rgba(255, 255, 255, 0.05)'
              }
              strokeDasharray={isChannelInUse ? '3 3' : '4 4'}
              strokeWidth={isChannelInUse ? 1.2 : 1}
              style={{ pointerEvents: 'none' }}
            />
            {canRenderText && (
              <text
                x={x}
                y={yLabelPos}
                fill={
                  isChannelInUse
                    ? theme.palette.white['06']
                    : 'rgba(255, 255, 255, 0.65)'
                }
                fontSize={isMobile ? 9 : band === '2.4GHz' ? 11 : 10}
                fontWeight={isChannelInUse ? 700 : 500}
                textAnchor="middle"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {labelText}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

// Displays a spectrum chart visualizing the signal strengths of Wi-Fi networks across channels
export function SpectrumChart({ networks }: SpectrumChartProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Filter States
  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState(true);
  const [rssiRange, setRssiRange] = useState<[number, number]>([-100, 0]);
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  const band = networks[0]?.band || '2.4GHz';

  // Available standard channels for current band
  const availableBandChannels = useMemo(() => {
    if (band === '2.4GHz') {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
    }
    return VALID_5GHZ_CHANNELS;
  }, [band]);

  // Filtered networks pipeline
  const filteredNetworks = useMemo(() => {
    return networks.filter((net) => {
      // hidden network filter
      if (!showHidden && !net.ssid) return false;

      // signal RSSI range (min and max)
      const isMinDefault = rssiRange[0] <= -100;
      const isMaxDefault = rssiRange[1] >= 0;
      if (!isMinDefault && net.signal < rssiRange[0]) return false;
      if (!isMaxDefault && net.signal > rssiRange[1]) return false;

      // specific channels selection
      if (
        selectedChannels.length > 0 &&
        !selectedChannels.includes(net.channel)
      ) {
        return false;
      }

      // text search by SSID or BSSID
      if (search.trim()) {
        const query = search.toLowerCase().trim();
        const matchSSID = net.ssid?.toLowerCase().includes(query);
        const matchBSSID = net.bssid?.toLowerCase().includes(query);
        if (!matchSSID && !matchBSSID) return false;
      }

      return true;
    });
  }, [networks, showHidden, rssiRange, selectedChannels, search]);

  const isFiltered =
    !showHidden ||
    rssiRange[0] > -100 ||
    rssiRange[1] < 0 ||
    selectedChannels.length > 0 ||
    search.trim() !== '';

  const activeFilterCount =
    (!showHidden ? 1 : 0) +
    (rssiRange[0] > -100 || rssiRange[1] < 0 ? 1 : 0) +
    (selectedChannels.length > 0 ? 1 : 0) +
    (search.trim() !== '' ? 1 : 0);

  const handleResetFilters = () => {
    setSearch('');
    setShowHidden(true);
    setRssiRange([-100, 0]);
    setSelectedChannels([]);
  };

  const toggleChannel = (ch: number) => {
    setSelectedChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]
    );
  };

  const chartMargin = useMemo(
    () => ({
      top: isMobile ? 8 : 12,
      right: isMobile ? 8 : 14,
      bottom: isMobile ? 26 : 30,
      left: isMobile ? 54 : 64,
    }),
    [isMobile]
  );

  const chartData = useMemo(
    () => buildSpectrumData(filteredNetworks),
    [filteredNetworks]
  );

  const series = useMemo(() => {
    return filteredNetworks.map((net, i) => {
      const baseColor = SPECTRUM_COLORS[i % SPECTRUM_COLORS.length];
      return {
        id: `net_${i}`,
        dataKey: `net_${i}`,
        showMark: false,
        color: baseColor,
        curve: 'monotoneX' as const,
        area: true,
        baseline: 'min' as const,
        label: net.ssid || t('scanner.hiddenNetwork'),
        valueFormatter: (value: number | null) => {
          if (value === null || value <= -99.9) {
            return null as unknown as string;
          }
          return `${value} dBm`;
        },
      };
    });
  }, [filteredNetworks, t]);

  const channelsInUse = useMemo(() => {
    return filteredNetworks
      .map((n) => n.channel)
      .filter((c) => !isNaN(c) && c > 0);
  }, [filteredNetworks]);

  const minCh =
    channelsInUse.length > 0
      ? Math.min(...channelsInUse)
      : band === '2.4GHz'
        ? 1
        : 36;
  const maxCh =
    channelsInUse.length > 0
      ? Math.max(...channelsInUse)
      : band === '2.4GHz'
        ? 14
        : 165;

  const xMin = band === '2.4GHz' ? 0.6 : Math.max(30, minCh - 3);
  const xMax = band === '2.4GHz' ? 14.4 : Math.max(165, maxCh + 3);

  const areaStyles = useMemo(
    () =>
      filteredNetworks.reduce(
        (acc, _, i) => {
          acc[`& .${areaElementClasses.root}.MuiAreaElement-series-net_${i}`] =
            {
              fill: `url(#gradient-${band}-${i}) !important`,
            };
          acc[`& .${lineElementClasses.root}.MuiLineElement-series-net_${i}`] =
            {
              strokeWidth: 2.5,
            };
          return acc;
        },
        {} as Record<string, React.CSSProperties>
      ),
    [filteredNetworks, band]
  );

  if (networks.length === 0) return null;

  return (
    <SpectrumChartBox>
      {/* Interactive Filters Bar */}
      <FilterBarContainer>
        {/* search and presets */}
        <FilterPresetsWrapper>
          <FilterSearchWrapper>
            <Search size={13} style={{ opacity: 0.6 }} />
            <input
              type="text"
              placeholder={t('scanner.filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <X
                size={13}
                style={{ cursor: 'pointer', opacity: 0.7 }}
                onClick={() => setSearch('')}
              />
            )}
          </FilterSearchWrapper>

          {/* preset: all */}
          <FilterToggleChip
            type="button"
            active={!isFiltered}
            onClick={handleResetFilters}
          >
            {t('scanner.filters.all')}
          </FilterToggleChip>

          {/* preset: Strong interference (> -70 dBm) */}
          <FilterToggleChip
            type="button"
            active={rssiRange[0] === -70 && rssiRange[1] === 0}
            onClick={() =>
              setRssiRange(
                rssiRange[0] === -70 && rssiRange[1] === 0
                  ? [-100, 0]
                  : [-70, 0]
              )
            }
          >
            {t('scanner.filters.strongNetworks')}
          </FilterToggleChip>

          {/* present: withou hidden */}
          <FilterToggleChip
            type="button"
            active={!showHidden}
            onClick={() => setShowHidden(!showHidden)}
          >
            {!showHidden ? <EyeOff size={13} /> : <Eye size={13} />}
            {t('scanner.filters.noHidden')}
          </FilterToggleChip>
        </FilterPresetsWrapper>

        {/*  Counter and Toggle Detailed Filters */}
        <FilterActionsWrapper>
          <FilterCountTypography variant="caption">
            {t('scanner.filters.showingCount', {
              count: filteredNetworks.length,
              total: networks.length,
            })}
          </FilterCountTypography>

          <FilterToggleChip
            type="button"
            active={showFiltersPanel || activeFilterCount > 0}
            onClick={() => setShowFiltersPanel(!showFiltersPanel)}
          >
            <SlidersHorizontal size={13} />
            {t('scanner.filters.title')}
            {activeFilterCount > 0 && (
              <LegendBadge>{activeFilterCount}</LegendBadge>
            )}
          </FilterToggleChip>

          {isFiltered && (
            <ClearFilterChip type="button" onClick={handleResetFilters}>
              <RotateCcw size={12} />
              {t('scanner.filters.clear')}
            </ClearFilterChip>
          )}
        </FilterActionsWrapper>
      </FilterBarContainer>

      {/* Collapsible Detailed Filter Panel */}
      <Collapse in={showFiltersPanel}>
        <FilterPanelCard>
          <FilterPanelMainRow>
            {/* RSSI Signal Range Slider (Min and Max) */}
            <SliderBox>
              <SliderHeaderRow>
                <FilterLabelTypography variant="caption">
                  {t('scanner.filters.signalRange')}
                </FilterLabelTypography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={theme.palette.primary.main}
                >
                  {rssiRange[0] === -100 && rssiRange[1] === 0
                    ? t('scanner.filters.allSignals')
                    : t('scanner.filters.rangeFormat', {
                        min: rssiRange[0],
                        max: rssiRange[1],
                      })}
                </Typography>
              </SliderHeaderRow>
              <StyledRssiSlider
                value={rssiRange}
                min={-100}
                max={0}
                step={5}
                disableSwap
                onChange={(_: Event, val: number | number[]) =>
                  setRssiRange(val as [number, number])
                }
                valueLabelDisplay="auto"
                valueLabelFormat={(v: number) => `${v} dBm`}
                marks={[
                  { value: -100, label: '-100' },
                  { value: -80, label: '-80' },
                  { value: -60, label: '-60' },
                  { value: -40, label: '-40' },
                  { value: -20, label: '-20' },
                  { value: 0, label: '0 dBm' },
                ]}
              />
            </SliderBox>

            {/* Hidden Networks Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <FilterLabelTypography variant="caption">
                  {t('scanner.filters.showHidden')}
                </FilterLabelTypography>
              }
            />
          </FilterPanelMainRow>

          {/* Channels Selection */}
          <Stack direction="column" gap="6px">
            <FilterLabelTypography variant="caption">
              {t('scanner.filters.bandChannels', { band })}
            </FilterLabelTypography>
            <ChannelsButtonRow>
              <FilterToggleChip
                type="button"
                active={selectedChannels.length === 0}
                onClick={() => setSelectedChannels([])}
              >
                {t('scanner.filters.allChannels')}
              </FilterToggleChip>
              {availableBandChannels.map((ch) => {
                const isSelected = selectedChannels.includes(ch);
                const hasNet = networks.some((n) => n.channel === ch);
                return (
                  <FilterToggleChip
                    key={ch}
                    type="button"
                    active={isSelected}
                    onClick={() => toggleChannel(ch)}
                    sx={{
                      opacity: hasNet ? 1 : 0.45,
                      fontWeight: isSelected || hasNet ? 700 : 500,
                    }}
                  >
                    {ch}
                  </FilterToggleChip>
                );
              })}
            </ChannelsButtonRow>
          </Stack>
        </FilterPanelCard>
      </Collapse>

      {/* Chart */}
      {filteredNetworks.length === 0 ? (
        <>
          {/** Empty state */}
          <EmptyStateBox>
            <WifiOff size={32} style={{ opacity: 0.5 }} />
            <Typography variant="body2" fontWeight={600}>
              {t('scanner.filters.noMatch')}
            </Typography>
            <ClearFilterChip
              type="button"
              onClick={handleResetFilters}
              sx={{
                mt: '6px',
                background: 'rgba(255, 255, 255, 0.05)',
              }}
            >
              <RotateCcw size={13} />
              {t('scanner.filters.clearAll')}
            </ClearFilterChip>
          </EmptyStateBox>
        </>
      ) : (
        <>
          <SpectrumChartContainer>
            {/** Gradient definitions for each network's area fill */}
            <GradientDefsSvg>
              <defs>
                {filteredNetworks.map((_, i) => {
                  const baseColor = SPECTRUM_COLORS[i % SPECTRUM_COLORS.length];
                  return (
                    <linearGradient
                      id={`gradient-${band}-${i}`}
                      key={i}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={baseColor}
                        stopOpacity={0.75}
                      />
                      <stop
                        offset="100%"
                        stopColor={baseColor}
                        stopOpacity={0.0}
                      />
                    </linearGradient>
                  );
                })}
              </defs>
            </GradientDefsSvg>

            {/** Spectrum line chart with horizontal channel indicators */}
            <StyledSpectrumLineChart
              dataset={chartData}
              xAxis={[
                {
                  dataKey: 'channel',
                  type: 'number',
                  min: xMin,
                  max: xMax,
                  disableLine: false,
                  disableTicks: true,
                  valueFormatter: () => '',
                },
              ]}
              yAxis={[
                {
                  min: -100,
                  max: 0,
                  tickNumber: isMobile ? 4 : 5,
                  valueFormatter: (v: number) => `${v} dBm`,
                  width: isMobile ? 54 : 64,
                },
              ]}
              series={series}
              margin={chartMargin}
              grid={{ horizontal: true }}
              tooltip={{ trigger: 'axis' }}
              slots={{
                legend: () => null,
                tooltip: ChartHoverCardTooltip,
              }}
              slotProps={{
                legend: { hidden: true },
              }}
              areastyles={areaStyles}
            >
              <HorizontalChannelLabels
                band={band}
                minCh={minCh}
                maxCh={maxCh}
                channelsInUse={channelsInUse}
              />
            </StyledSpectrumLineChart>
          </SpectrumChartContainer>

          {/* chart legend with network details */}
          <BottomLegendContainer>
            <LegendHeaderRow>
              <LegendTitleTypography variant="caption">
                {t('scanner.spectrumTitle')} ({filteredNetworks.length})
              </LegendTitleTypography>
            </LegendHeaderRow>
            <LegendChipsWrapper>
              {filteredNetworks.map((net, i) => {
                const baseColor = SPECTRUM_COLORS[i % SPECTRUM_COLORS.length];
                const isHidden = !net.ssid;
                const displayName = net.ssid || t('scanner.hiddenNetwork');

                const legendCard = (
                  <LegendNetworkCard>
                    <Stack direction="column" spacing="6px">
                      {/* Network Header */}
                      <LegendCardHeader>
                        <TooltipDotMarker
                          color={baseColor}
                          sx={{ width: 9, height: 9 }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={baseColor}
                          noWrap
                        >
                          {displayName}
                        </Typography>
                      </LegendCardHeader>

                      {/* Details */}
                      <FilterLabelTypography variant="caption">
                        <strong>{t('scanner.columns.bssid')}:</strong>{' '}
                        {net.bssid}
                      </FilterLabelTypography>

                      <FilterLabelTypography variant="caption">
                        <strong>{t('scanner.columns.channel')}:</strong>{' '}
                        {net.channel} &nbsp;|&nbsp;{' '}
                        <strong>{t('scanner.columns.bandwidth')}:</strong>{' '}
                        {net.bandwidth || '20MHz'}
                      </FilterLabelTypography>

                      <FilterLabelTypography variant="caption">
                        <strong>{t('scanner.filters.signalIntensity')}</strong>{' '}
                        {net.signal} dBm
                      </FilterLabelTypography>
                    </Stack>
                  </LegendNetworkCard>
                );

                return (
                  <DescriptionPopper
                    key={`${net.bssid}_${i}`}
                    placement="top"
                    offset={[-45, 14]}
                    description={legendCard}
                  >
                    <LegendChipItem>
                      <NetworkLegendColor background={baseColor} />
                      <LegendChipLabel variant="caption">
                        {isHidden ? (
                          <HiddenSSIDLabel>
                            {t('scanner.hiddenNetwork')}
                          </HiddenSSIDLabel>
                        ) : (
                          net.ssid
                        )}
                      </LegendChipLabel>
                      <LegendBadge>
                        {t('scanner.channelPrefix', {
                          channel: net.channel,
                        })}
                      </LegendBadge>
                      <LegendSignalTypography variant="caption">
                        {net.signal} dBm
                      </LegendSignalTypography>
                    </LegendChipItem>
                  </DescriptionPopper>
                );
              })}
            </LegendChipsWrapper>
          </BottomLegendContainer>
        </>
      )}
    </SpectrumChartBox>
  );
}
